import { Injectable, NgZone, OnDestroy } from '@angular/core';
import Swal from 'sweetalert2';
import { forceSignOutRedirect } from '../utils/force-sign-out';

// HIPAA 164.312(a)(2)(iii) / HITRUST AC-12 automatic-logoff for the web portal
// -- the equivalent of the mobile app's AppUnlockService 15-min guard. The
// portal previously had only a Duende-bound expiry dialog, which is inert for
// Cognito-only sessions; this is activity-based and session-agnostic.
//
// Flow: after (TIMEOUT - WARN_LEAD) of no activity, a modal "Are you still
// there?" warning appears with a live countdown. "Stay signed in" restarts the
// idle window; no response (countdown ends) or "Sign out now" forces a clean
// sign-out + redirect. Only acts from an authenticated surface.
@Injectable({ providedIn: 'root' })
export class InactivityService implements OnDestroy {
  // HIPAA 164.312(a)(2)(iii) automatic-logoff. 30 min is the NIST 800-63B AAL2
  // inactivity ceiling -- a documented, risk-based relaxation vs the mobile
  // app's 15 min, justified by the admin-portal context + high web re-auth
  // friction (full TOTP, no biometric). Warn 1 minute before signing out.
  private static readonly TIMEOUT_MS = 30 * 60 * 1000;
  private static readonly WARN_LEAD_MS = 60 * 1000;

  private static readonly ACTIVITY_EVENTS = [
    'mousedown',
    'mousemove',
    'keydown',
    'scroll',
    'touchstart',
    'click',
  ];

  private warnTimer: ReturnType<typeof setTimeout> | null = null;
  private started = false;
  private warningOpen = false;
  private readonly onActivity = (): void => {
    // While the "are you still there?" dialog is up, only its buttons decide --
    // background activity must not silently cancel the confirmation.
    if (this.warningOpen) return;
    this.reset();
  };

  constructor(private zone: NgZone) {}

  // Idempotent -- AppComponent calls this on every bootstrap.
  start(): void {
    if (this.started) return;
    this.started = true;
    // Listeners + timers live OUTSIDE Angular so a stream of mousemove events
    // doesn't trigger change detection on every pixel.
    this.zone.runOutsideAngular(() => {
      InactivityService.ACTIVITY_EVENTS.forEach((evt) =>
        document.addEventListener(evt, this.onActivity, { passive: true }),
      );
      this.reset();
    });
  }

  stop(): void {
    if (!this.started) return;
    this.started = false;
    InactivityService.ACTIVITY_EVENTS.forEach((evt) =>
      document.removeEventListener(evt, this.onActivity),
    );
    this.clearWarnTimer();
  }

  private clearWarnTimer(): void {
    if (this.warnTimer) clearTimeout(this.warnTimer);
    this.warnTimer = null;
  }

  private reset(): void {
    this.clearWarnTimer();
    this.warnTimer = setTimeout(
      () => this.showWarning(),
      InactivityService.TIMEOUT_MS - InactivityService.WARN_LEAD_MS,
    );
  }

  private showWarning(): void {
    // Never interrupt the sign-in flow (HashLocationStrategy -> location.hash).
    if (window.location.hash.startsWith('#/auth')) {
      this.reset();
      return;
    }
    this.warningOpen = true;

    let interval: ReturnType<typeof setInterval> | undefined;
    Swal.fire({
      title: 'Are you still there?',
      html:
        `For your security you'll be signed out in ` +
        `<b>${InactivityService.WARN_LEAD_MS / 1000}</b> seconds due to inactivity.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Stay signed in',
      cancelButtonText: 'Sign out now',
      confirmButtonColor: '#00bcd4',
      allowOutsideClick: false,
      allowEscapeKey: false,
      timer: InactivityService.WARN_LEAD_MS,
      timerProgressBar: true,
      didOpen: () => {
        const counter = Swal.getPopup()?.querySelector('b');
        interval = setInterval(() => {
          const left = Swal.getTimerLeft();
          if (counter && left != null) {
            counter.textContent = String(Math.ceil(left / 1000));
          }
        }, 250);
      },
      willClose: () => {
        if (interval) clearInterval(interval);
      },
    }).then((result) => {
      this.warningOpen = false;
      if (result.isConfirmed) {
        this.reset(); // "Stay signed in" -> restart the full idle window
      } else {
        // Countdown elapsed, "Sign out now", or any other dismiss -> log out.
        void forceSignOutRedirect('idle');
      }
    });
  }

  ngOnDestroy(): void {
    this.stop();
  }
}
